import React, { useEffect, useLayoutEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { Boxes, ClipboardCheck, MapPin, Package, ShoppingBag } from 'lucide-react';
import { Button } from '../../../../components/ui/Button';
import { OperatorScanPanel } from '../../components/OperatorScanPanel';
import { StockDetail } from './StockDetail';
import { taskEntered } from '../../../../features/operator/operatorDeviceSlice';
import {
  lookupCleared,
  lookupEntered,
  selectLoadingStockKey,
  selectShownStockKey,
} from '../../../../features/operator/operatorSlice';
import {
  fetchStock,
  lookupCode,
  stockTargetKey,
  stockTargetOf,
} from '../../../../features/operator/operatorThunks';
import { OPERATOR_FLOW, OPERATOR_FLOW_PATHS } from '../../../../features/operator/operatorConstants';
import { SCAN_SOURCE } from '../../../../features/operator/operatorScanGate';
import {
  orderCardTitle,
  orderLabelsLink,
  orderNextStepText,
  orderOpenLink,
  returnState,
  selectCourierSendReady,
  selectEnabledFlows,
} from '../../../../features/operator/operatorUtils';
import { OPEN_RECEIPT_STATUSES } from '../../../../features/operatorReceiving/operatorReceivingUtils';
import { supplyKindLabel } from '../../../../features/supplies/suppliesUtils';
import { receiptStatusLabel, wmsOrderStateLabel, zoneTypeLabel } from '../../../../utils/inventoryLabels';

const KINDS = {
  slot: { icon: MapPin, label: 'Ubicación' },
  product: { icon: Package, label: 'Producto' },
  receipt: { icon: ClipboardCheck, label: 'Recepción' },
  supply: { icon: Boxes, label: 'Insumo' },
  order: { icon: ShoppingBag, label: 'Orden' },
};

/** An order's label, made or printed from here, brings the operator back to Consulta. */
const FROM_LOOKUP = returnState(OPERATOR_FLOW_PATHS[OPERATOR_FLOW.STOCK], 'Volver a Consulta');

/** The link that opens a result where it is worked, as «Abrir recepción» does; `null` for none. */
const openLinkOf = (result, flows, courierSend) => {
  if (result.kind === 'receipt') {
    const open = flows.includes(OPERATOR_FLOW.RECEIVING) && OPEN_RECEIPT_STATUSES.includes(result.status);
    return open ? { to: `/receiving/${result.receipt_id}`, label: 'Abrir recepción' } : null;
  }
  return result.kind === 'order' ? orderOpenLink(result, flows, { courierSend, from: FROM_LOOKUP }) : null;
};

/** A result's handle in the list: two kinds can share an id. */
const resultKey = (result) =>
  `${result.kind}-${result.slot_id ?? result.product_id ?? result.receipt_id ?? result.supply_id ?? result.order_id}`;

const describe = (result) => {
  switch (result.kind) {
    case 'slot':
      return {
        title: result.name || result.code,
        lines: [result.code, zoneTypeLabel(result.zone_type), result.is_active ? 'Activa' : 'Inactiva'],
      };
    case 'product':
      return {
        title: `${result.sku} — ${result.name}`,
        lines: [result.company_name, Number(result.factor) > 1 ? `Caja ×${result.factor}` : null],
      };
    case 'receipt':
      return { title: result.code, lines: [receiptStatusLabel(result.status), result.company_name] };
    case 'order':
      return { title: orderCardTitle(result), lines: [wmsOrderStateLabel(result.wms_state), result.company_name] };
    default:
      return { title: `${result.sku} — ${result.name}`, lines: [supplyKindLabel(result.supply_kind)] };
  }
};

const ResultCard = ({ result, stockTarget, flows, courierSend, busy, loadingStock, onStock }) => {
  const { icon: Icon, label } = KINDS[result.kind] || KINDS.product;
  const { title, lines } = describe(result);
  const open = openLinkOf(result, flows, courierSend);
  // A labeled order's labels, to print them again.
  const labels = result.kind === 'order' ? orderLabelsLink(result, flows, { from: FROM_LOOKUP }) : null;

  return (
    <div
      data-testid="lookup-result"
      data-kind={result.kind}
      className="rounded-2xl border border-border bg-white p-4 shadow-sm dark:bg-[#1a1a1a]"
    >
      <div className="flex items-start gap-3">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-ninja-gray text-ninja-dark dark:bg-[#2d2d2d] dark:text-white">
          <Icon size={22} />
        </span>
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-wide text-text-muted">{label}</p>
          <p data-testid="lookup-result-title" className="break-words text-base font-bold text-ninja-dark dark:text-white">
            {title}
          </p>
          <p className="text-sm text-text-secondary">{lines.filter(Boolean).join(' · ')}</p>
        </div>
      </div>
      {open && (
        <Link
          to={open.to}
          state={open.state}
          data-testid="lookup-open"
          className="mt-3 block rounded-xl bg-ninja-green px-4 py-3 text-center font-bold text-white"
        >
          {open.label}
        </Link>
      )}
      {labels && (
        <Link
          to={labels.to}
          state={labels.state}
          data-testid="lookup-labels"
          className="mt-3 block rounded-xl border-2 border-ninja-green px-4 py-2.5 text-center font-bold text-ninja-green dark:text-white"
        >
          {labels.label}
        </Link>
      )}
      {result.kind === 'order' && !open && (
        <p data-testid="lookup-order-next" className="mt-3 text-sm font-semibold text-ninja-dark dark:text-white">
          {orderNextStepText(result)}
        </p>
      )}
      {stockTarget && (
        // Not a PendingButton: a request that was replaced stays in flight until it answers or times out (120 s).
        <Button
          variant="outline"
          size="lg"
          fullWidth
          loading={loadingStock}
          aria-busy={loadingStock}
          disabled={busy}
          loadingLabel="Cargando stock…"
          onClick={() => onStock(stockTarget)}
          data-testid="lookup-stock"
          data-kind={result.kind}
          className="mt-3"
        >
          Ver stock
        </Button>
      )}
    </div>
  );
};

/**
 * Consulta: what a code is in this warehouse — a slot, a product, a receipt,
 * a supply — and, for a product or a slot, its stock. The one flow the Lite
 * module gives. Every match is shown: here an ambiguous code is the answer,
 * not a problem.
 */
export const StockLookup = () => {
  const dispatch = useDispatch();
  const { lookup } = useSelector((state) => state.operator);
  const flows = useSelector(selectEnabledFlows);
  const courierSend = useSelector(selectCourierSendReady);
  const shownStockKey = useSelector(selectShownStockKey);
  const loadingStockKey = useSelector(selectLoadingStockKey);

  // Before the first paint: the figures shown last time may have moved since.
  useLayoutEffect(() => {
    dispatch(lookupEntered());
  }, [dispatch]);

  useEffect(() => {
    dispatch(taskEntered({ flow: OPERATOR_FLOW.STOCK }));
  }, [dispatch]);

  const read = (code, source) => dispatch(lookupCode({ code, source }));

  return (
    <div className="space-y-5">
      <OperatorScanPanel
        prompt="Escanea cualquier código"
        hint={
          // The scan looks for orders only where picking, packing or dispatch is open.
          [OPERATOR_FLOW.PICKING, OPERATOR_FLOW.PACKING, OPERATOR_FLOW.DISPATCH].some((flow) => flows.includes(flow))
            ? 'Una ubicación, un producto, una guía de recepción, un insumo o una orden.'
            : 'Una ubicación, un producto, una guía de recepción o un insumo.'
        }
        accepting={!lookup.loading}
        lastCode={lookup.lastCode}
        busyLabel="Buscando…"
        onCode={read}
      />

      {lookup.code && (
        <section className="space-y-3">
          <div className="flex items-center justify-between gap-3">
            <h2 className="truncate text-base font-bold text-ninja-dark dark:text-white">{lookup.code}</h2>
            <Button variant="ghost" size="lg" onClick={() => dispatch(lookupCleared())} data-testid="lookup-clear">
              Limpiar
            </Button>
          </div>
          {lookup.error ? (
            <p data-testid="lookup-error" className="rounded-2xl border border-border bg-white p-4 text-sm text-text-secondary dark:bg-[#1a1a1a]">
              {lookup.error.message}
            </p>
          ) : (
            <ul className="space-y-3">
              {lookup.results.map((result) => {
                const stockTarget = stockTargetOf(result);
                const stockKey = stockTarget ? stockTargetKey(stockTarget) : null;
                return (
                  <li key={resultKey(result)} className="space-y-3">
                    <ResultCard
                      result={result}
                      stockTarget={stockTarget}
                      flows={flows}
                      courierSend={courierSend}
                      busy={lookup.loading}
                      loadingStock={stockKey !== null && stockKey === loadingStockKey}
                      onStock={(target) => dispatch(fetchStock(target))}
                    />
                    {/* Right below the result it is of: at the end of the list it falls below the fold. */}
                    {stockKey !== null && stockKey === shownStockKey && <StockDetail />}
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      )}

      {lookup.history.length > 0 && (
        <section className="space-y-2">
          <h2 className="text-sm font-semibold text-text-secondary">Últimos códigos</h2>
          {/* A chip fills its `li`, which is as wide as the code. */}
          <ul className="flex flex-wrap gap-2">
            {lookup.history.map((code) => (
              <li key={code}>
                <Button
                  variant="choice"
                  pill
                  data-testid="lookup-history-item"
                  disabled={lookup.loading}
                  onClick={() => read(code, SCAN_SOURCE.TAP)}
                >
                  <span className="text-sm font-semibold">{code}</span>
                </Button>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
};
